import { useMemo, useCallback } from 'react';

import { toZonedTime } from 'date-fns-tz';
import dayjs from 'dayjs';

import { useAppSelector } from '../../store/hooks';
import { DEFAULT_TIME_ZONE } from '../../utils/time-zone-utils';

interface TimeZoneHook {
  toSystemTime: (utcString: Date | string) => Date;
  toFormattedSystemTime: (utcString: Date | string | undefined) => string;
  showSystemTime: (utcString: Date | string | undefined) => string;
  getGmtModifier: (displayTimeZone?: string, referenceDate?: Date) => string;
}

const buildFormatter = (timeZone: string) => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const formatOffset = (offsetMinutes: number) => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0');
  const minutes = String(absoluteMinutes % 60).padStart(2, '0');
  return `GMT${sign}${hours}:${minutes}`;
};

const fallbackOffset = (referenceDate: Date, timeZone: string) => {
  const tzDate = new Date(referenceDate.toLocaleString('en-US', { timeZone }));
  const offsetMinutes = -(Math.round((referenceDate.getTime() - tzDate.getTime()) / 60000) + referenceDate.getTimezoneOffset());
  return formatOffset(offsetMinutes);
};

const parseShortOffset = (tzName: string): number | null => {
  const match = tzName.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!match) {
    return null;
  }
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] ?? '0', 10);
  return sign * (hours * 60 + minutes);
};

export const useTimeZone = (): TimeZoneHook => {
  const { profile } = useAppSelector((gs) => gs.systemProfileState);
  const systemTimeZone = profile?.displayTimeZone ?? DEFAULT_TIME_ZONE;
  const formatter = useMemo(() => buildFormatter(systemTimeZone), [systemTimeZone]);

  const toSystemTime = useCallback(
    (utcString: Date | string): Date => {
      const utcDate = new Date(utcString);
      return toZonedTime(utcDate, systemTimeZone);
    },
    [systemTimeZone]
  );

  const toFormattedSystemTime = useCallback(
    (utcString: string | Date | undefined) => {
      if (!utcString) {
        return 'N/A';
      }
      const tzDt = toSystemTime(utcString);
      return dayjs(tzDt).format('MM/DD hh:mm a');
    },
    [toSystemTime]
  );

  const showSystemTime = useCallback(
    (utcString: Date | string | undefined): string => {
      if (!utcString) {
        return '';
      }
      const utcDate = new Date(utcString);
      return formatter.format(utcDate);
    },
    [formatter]
  );

  const getGmtModifier = (displayTimeZone = DEFAULT_TIME_ZONE, referenceDate: Date = new Date()): string => {
    try {
      const formatter = buildFormatter(displayTimeZone);
      const tzName = formatter.formatToParts(referenceDate).find((part) => part.type === 'timeZoneName')?.value;
      const offsetMinutes = tzName ? parseShortOffset(tzName) : null;
      if (offsetMinutes !== null) {
        return formatOffset(offsetMinutes);
      }
    } catch {}
    return fallbackOffset(referenceDate, displayTimeZone);
  };

  return {
    toSystemTime,
    toFormattedSystemTime,
    showSystemTime,
    getGmtModifier
  };
};

export default useTimeZone;
